'use client';

import { DEFAULT_DRAW_ID } from '@svenskaspelab/poolbetting/core/lib';
import { getValidatedQueryParameters } from '@svenskaspelab/poolbetting/core/lib/client';
import type { DrawNumber } from '@svenskaspelab/poolbetting/schema/draw';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

type LegacyDrawRedirectProps = {
  openDrawNumbers: DrawNumber[];
  drawNumber: DrawNumber | typeof DEFAULT_DRAW_ID;
};

/**
 * Handles redirects from legacy `?draw=drawNumber` query-parameter URLs.
 *
 * When the current `[drawId]` segment could not be parsed (i.e. `drawNumber` is
 * `DEFAULT_DRAW_ID`) but a valid `?draw=` query parameter points to an opened
 * draw, replace the URL with `basePath/drawNumber`.
 *
 * @param root0 component props
 * @param root0.openDrawNumbers Opened draw numbers used to validate the `?draw=` parameter.
 * @param root0.drawNumber The parsed draw number from the current route segment.
 * @returns Nothing — this component only triggers a client-side redirect.
 */
export default function LegacyDrawRedirect({ openDrawNumbers, drawNumber }: LegacyDrawRedirectProps): undefined {
  const router = useRouter();
  const searchParams = useSearchParams();
  const drawFromQuery = getValidatedQueryParameters('draw')(searchParams).draw;

  useEffect(() => {
    if (drawNumber !== DEFAULT_DRAW_ID || drawFromQuery === undefined) {
      return;
    }

    if (!openDrawNumbers.includes(drawFromQuery)) {
      return;
    }

    // Normalize basePath so it always starts with `/` and never ends with `/`,
    // regardless of how `NEXT_PUBLIC_BASE_PATH` is configured (e.g. `powerplay`,
    // `/powerplay`, or `/powerplay/`).
    const rawBasePath = process.env.NEXT_PUBLIC_BASE_PATH?.trim() ?? '';
    const trimmedBasePath = rawBasePath.replace(/\/+$/, '');
    const basePath =
      trimmedBasePath === '' || trimmedBasePath.startsWith('/') ? trimmedBasePath : `/${trimmedBasePath}`;

    const qs = new URLSearchParams(searchParams.toString());
    qs.delete('draw');

    const newUrl = qs.toString() ? `${basePath}/${drawFromQuery}?${qs.toString()}` : `${basePath}/${drawFromQuery}`;

    router.replace(newUrl);
    // `searchParams` changes as a result of the `router.replace` above, so we
    // deliberately exclude it from the deps to avoid re-running this effect on
    // the post-redirect render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawNumber, drawFromQuery, openDrawNumbers, router]);

  return undefined;
}
